import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { format } from "date-fns";
import { CalendarIcon, ArrowRight, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { PLANS, TIME_SLOTS, BRAND } from "@/lib/data";
import { MaskedLines } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Booking = () => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", plan: "", message: "" });
  const [date, setDate] = useState(null);
  const [time, setTime] = useState("");
  const [calOpen, setCalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const update = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      toast.error("Please fill in your name, email and phone.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/consultations`, {
        name: form.name,
        email: form.email,
        phone: form.phone,
        plan: form.plan || null,
        preferred_date: date ? format(date, "yyyy-MM-dd") : null,
        preferred_time: time || null,
        message: form.message || null,
      });
      toast.success("Request received. Our team will reach out within one business day.");
      setForm({ name: "", email: "", phone: "", plan: "", message: "" });
      setDate(null);
      setTime("");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="relative bg-[#FAFAF8] py-24 lg:py-40" data-testid="contact-section">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          {/* Left copy */}
          <div className="lg:col-span-5">
            <p className="eyebrow text-[#C89933] mb-4">Book a Consultation</p>
            <h2 className="font-serif-display text-[#0F1E3D] text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
              <MaskedLines lines={["Let's look at your", "wealth today, and", "build the plan for it."]} />
            </h2>
            <p className="mt-4 text-[#4A5568] text-base lg:text-lg leading-relaxed font-light max-w-md">
              Share a few details and pick a time that suits you. Your first conversation is complimentary and entirely confidential.
            </p>

            <div className="mt-12 space-y-6">
              <div className="flex items-center gap-3 text-[#7F8D76]">
                <ShieldCheck className="h-5 w-5" strokeWidth={1.6} />
                <span className="overline text-[0.62rem] text-[#4A5568]">{BRAND.sebi}</span>
              </div>
              <div className="pt-8 border-t border-[#E2E8F0] space-y-2 text-[#4A5568] text-sm font-light">
                <p data-testid="contact-person" className="text-[#0F1E3D] font-medium">{BRAND.contactPerson}</p>
                <p data-testid="contact-email">{BRAND.email}</p>
                <p data-testid="contact-whatsapp">
                  <a href={`https://wa.me/${BRAND.whatsapp}`} target="_blank" rel="noopener noreferrer" className="hover:text-[#25D366] transition-colors duration-300">
                    WhatsApp · {BRAND.whatsappDisplay}
                  </a>
                </p>
                <p data-testid="contact-location">{BRAND.location}</p>
              </div>
              <a
                href="/discovery"
                className="mt-4 inline-flex items-center gap-2 text-sm font-medium text-[#0F1E3D] group"
                data-testid="discovery-link"
              >
                <span className="border-b border-[#C89933]/60 group-hover:border-[#C89933] transition-colors">
                  Or take the 5-minute Client Discovery
                </span>
                <span className="text-[#C89933] transition-transform duration-300 group-hover:translate-x-1">→</span>
              </a>
            </div>
          </div>

          {/* Form card */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="lg:col-span-7"
          >
            <form
              onSubmit={handleSubmit}
              className="bg-white border border-[#E2E8F0] rounded-sm p-8 lg:p-12"
              data-testid="booking-form"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="name" className="overline text-[0.62rem] text-[#0F1E3D]">Full Name</Label>
                  <Input id="name" value={form.name} onChange={update("name")} placeholder="Jane Mehta" className="rounded-sm border-[#E2E8F0] h-12 focus-visible:ring-[#C89933]" data-testid="input-name" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email" className="overline text-[0.62rem] text-[#0F1E3D]">Email</Label>
                  <Input id="email" type="email" value={form.email} onChange={update("email")} placeholder="jane@company.com" className="rounded-sm border-[#E2E8F0] h-12 focus-visible:ring-[#C89933]" data-testid="input-email" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone" className="overline text-[0.62rem] text-[#0F1E3D]">Phone</Label>
                  <Input id="phone" value={form.phone} onChange={update("phone")} placeholder="+91 98765 43210" className="rounded-sm border-[#E2E8F0] h-12 focus-visible:ring-[#C89933]" data-testid="input-phone" />
                </div>
                <div className="space-y-2">
                  <Label className="overline text-[0.62rem] text-[#0F1E3D]">Plan of Interest</Label>
                  <Select value={form.plan} onValueChange={(v) => setForm((f) => ({ ...f, plan: v }))}>
                    <SelectTrigger className="rounded-sm border-[#E2E8F0] h-12 focus:ring-[#C89933]" data-testid="select-plan">
                      <SelectValue placeholder="Select a plan" />
                    </SelectTrigger>
                    <SelectContent>
                      {PLANS.map((p) => (
                        <SelectItem key={p.id} value={p.name} data-testid={`plan-option-${p.id}`}>
                          {p.name} · {p.concept}
                        </SelectItem>
                      ))}
                      <SelectItem value="Not sure yet">Not sure yet</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="overline text-[0.62rem] text-[#0F1E3D]">Preferred Date</Label>
                  <Popover open={calOpen} onOpenChange={setCalOpen}>
                    <PopoverTrigger asChild>
                      <button
                        type="button"
                        className="w-full h-12 rounded-sm border border-[#E2E8F0] px-3 text-left text-sm flex items-center gap-2 hover:border-[#C89933] transition-colors duration-300"
                        data-testid="date-trigger"
                      >
                        <CalendarIcon className="h-4 w-4 text-[#7F8D76]" />
                        {date ? format(date, "PPP") : <span className="text-[#4A5568]/60">Pick a date</span>}
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={date}
                        onSelect={(d) => { setDate(d); setCalOpen(false); }}
                        disabled={(d) => d < new Date(new Date().setHours(0, 0, 0, 0))}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="space-y-2">
                  <Label className="overline text-[0.62rem] text-[#0F1E3D]">Preferred Time</Label>
                  <Select value={time} onValueChange={setTime}>
                    <SelectTrigger className="rounded-sm border-[#E2E8F0] h-12 focus:ring-[#C89933]" data-testid="select-time">
                      <SelectValue placeholder="Select a time" />
                    </SelectTrigger>
                    <SelectContent>
                      {TIME_SLOTS.map((slot) => (
                        <SelectItem key={slot} value={slot} data-testid={`time-option-${slot.replace(/[:\s]/g, "")}`}>
                          {slot}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2 mt-6">
                <Label htmlFor="message" className="overline text-[0.62rem] text-[#0F1E3D]">Anything we should know? (Optional)</Label>
                <Textarea id="message" value={form.message} onChange={update("message")} placeholder="A little about your goals..." rows={4} className="rounded-sm border-[#E2E8F0] focus-visible:ring-[#C89933] resize-none" data-testid="input-message" />
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="group mt-8 w-full inline-flex items-center justify-center gap-3 rounded-full bg-[#0F1E3D] px-8 py-4 text-[#FAFAF8] font-medium hover:bg-[#C89933] hover:text-[#0F1E3D] transition-colors duration-400 disabled:opacity-60"
                data-testid="booking-submit"
              >
                {submitting ? (
                  <><Loader2 className="h-5 w-5 animate-spin" /> Sending...</>
                ) : (
                  <>Request Consultation <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1.5" strokeWidth={1.8} /></>
                )}
              </button>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Booking;
